/**
 * F-16 clipboard TSV: copy/paste between Kola and Google Sheets / Excel.
 *
 * Both apps put a TSV rendition on the clipboard where fields containing
 * tabs, newlines, or quotes are wrapped in double quotes with `""` escaping
 * (Excel's dialect). These helpers are pure so the grid's copy/paste handlers
 * stay trivially testable.
 */
import { parseDelimited } from "./delimited.js";

function tsvField(value: string): string {
  return /[\t\n\r"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/** Serialize a matrix to Excel-compatible TSV (no trailing newline). */
export function matrixToTsv(rows: readonly (readonly string[])[]): string {
  return rows.map((row) => row.map(tsvField).join("\t")).join("\n");
}

/**
 * Parse clipboard TSV into a matrix. Handles quoted multi-line fields
 * (`"a\nb"`), doubled-quote escapes, and both \n and \r\n row separators.
 * A single trailing newline (Excel appends one) does not produce an extra
 * empty row.
 *
 * The grammar is `delimited.ts` with a tab — the same one the F-90 script
 * import reads CSV/TSV files with, so the clipboard and a saved file can never
 * disagree about what `"a,""b"""` means.
 */
export function parseTsv(text: string): string[][] {
  return parseDelimited(text, "\t");
}
